import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AutomationErrorHandlerService, ValidationErrorData } from '../../services';
import { CommonDisplayActionService } from './common-display-action.service';
import { CommonDisplayActivityService } from './common-display-activity.service';

/**
 * An entry in the activity table. This is used in the step and activity columns.
 */
export interface ActivityTableEntry {
  /** The emphasized title in the table entry. */
  title$: Observable<string>;

  /** The small text in the table entry. */
  body$: Observable<string>;
}

/**
 * Provides entries for the activity table.
 */
export interface ActivityTableEntryService {
  /** Gets text for the activity column in the table. */
  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation?: AutomationInterface,
  ): ActivityTableEntry;
}

/**
 * Data that a WorkflowNodeService provides for a workflow editor node.
 * If the text is a placeholder used due to an error in generating the desired text,
 * the `validationError` field should be provided. */
export interface NodeData {
  /** The text to display, regardless of validation errors detected. */
  displayContents: Observable<string>;

  /**
   * Any validation errors that occurred. Note: this is NOT marked as optional to force people to indicate whether an error occurred.
   * We ought to be able to display this in the DisplayService rather than force every node to do error handling directly.
   */
  validationError: ValidationErrorData | undefined;
}

/**
 * Provides text for the automation editor.
 */
export interface WorkflowNodeService {
  /**
   * The information to display in an automation node (i.e. the nodes in the graph).
   * If a validation error is detected, it can be returned through the `NodeText` object.
   * This allows whatever is interacting with the node to display the error as it wishes.
   */
  getAutomationNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntilNode: boolean,
    automation?: AutomationInterface,
  ): Observable<NodeData>;

  /**
   * The information to display in a template node (i.e. the nodes in the graph)
   * If a validation error is detected, it can be returned through the `NodeText` object.
   * This allows whatever is interacting with the node to display the error as it wishes.
   */
  getTemplateNodeData?(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntilNode: boolean,
    automation?: AutomationInterface,
  ): Observable<NodeData>;
}

/**
 * A convenience class to make it simpler to implement trigger/action display services.
 */
export abstract class AutomationDisplayService
  implements ActivityTableEntryService, WorkflowNodeService, CommonDisplayActivityService, CommonDisplayActionService
{
  protected readonly translateService: TranslateService = inject(TranslateService);
  protected readonly errorHandler: AutomationErrorHandlerService = inject(AutomationErrorHandlerService);

  abstract getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation?: AutomationInterface,
  ): ActivityTableEntry;

  abstract getAutomationNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntilNode: boolean,
    automation?: AutomationInterface,
  ): Observable<NodeData>;

  /**
   * Returns node data in a more concise format.
   * @see noValidationErrorsDetected
   * @see validationErrorsDetected
   * */
  protected nodeDisplayData(
    displayText$: Observable<string>,
    error: ValidationErrorData | undefined,
  ): Observable<NodeData> {
    return of({
      displayContents: displayText$,
      validationError: error,
    });
  }

  /**
   * Readability method.
   * @see nodeDisplayData
   * */
  protected noValidationErrorsDetected(): undefined {
    return undefined;
  }

  /**
   * Readability method.
   * @see nodeDisplayData
   * */
  protected validationErrorsDetected(
    step: WorkflowStepInterface,
    message: string,
    isNonDismissible: boolean,
  ): ValidationErrorData {
    return {
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: message,
      isNonDismissable: isNonDismissible,
    };
  }

  /**
   * Readability method.
   * @see nodeDisplayData
   * */
  protected nonDismissibleValidationErrorsDetected(step: WorkflowStepInterface, message: string): ValidationErrorData {
    return {
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: message,
      isNonDismissable: true,
    };
  }

  /** Readability method. */
  protected emptyBody(): Observable<string> {
    return of('');
  }

  /** Returns a table entry. */
  protected tableEntry(title$: Observable<string>, body$: Observable<string>): ActivityTableEntry {
    return {
      title$: title$,
      body$: body$,
    };
  }

  /** Returns a table entry specifically for trigger activity. */
  protected triggerActivityEntry(body$: Observable<string>): ActivityTableEntry {
    return this.tableEntry(this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION'), body$);
  }

  /** By default, the step column body is the same as the automation node text. */
  protected getDefaultStepColumnBody(
    namespace: string,
    step: WorkflowStepInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getAutomationNodeData(namespace, step, false, automation).pipe(
      switchMap((data) => data.displayContents),
    );
  }

  /** By default, the template node data is the same as that of the automation editor. */
  public getTemplateNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntilNode: boolean,
    automation?: AutomationInterface,
  ): Observable<NodeData> {
    return this.getAutomationNodeData(namespace, step, isWaitUntilNode, automation);
  }

  getActionSubtitle(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
    automation?: AutomationInterface,
  ): Observable<string> {
    return this.getAutomationNodeData(namespace, step, isWaitUntil || false, automation).pipe(
      // TODO: Handle errors here until the code using this service takes over error handling duties.
      tap((data) => {
        if (data.validationError) {
          this.errorHandler.setValidationError(data.validationError);
        }
      }),
      switchMap((data) => data.displayContents),
    );
  }

  // These are only used internally, so it's safe for them to be empty.
  getActionSubtitleEmptyState(_step: WorkflowStepInterface, _isWaitUntil?: boolean, _params?: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_params: any, _step: WorkflowStepInterface, _isWaitUntil?: boolean): Observable<string> {
    return of('');
  }

  getActionSubtitleSuccessState(_params: any, _isWaitUntil?: boolean): Observable<string> {
    return of('');
  }

  getActivitySubtitle(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getActivityColumn(namespace, activity, automation).body$;
  }

  getActivityTitle(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getActivityColumn(namespace, activity, automation).title$;
  }

  getTemplateActionSubtitle(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
    automation?: AutomationInterface,
  ): Observable<string> {
    return this.getTemplateNodeData(namespace, step, isWaitUntil || false, automation).pipe(
      // TODO: Handle errors here until the code using this service takes over error handling duties.
      tap((data) => {
        if (data.validationError) {
          this.errorHandler.setValidationError(data.validationError);
        }
      }),
      switchMap((data) => data.displayContents),
    );
  }
}
